import { useEffect, useState, FormEvent } from "react";
import { Link } from "react-router-dom";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Contact Us – Dental Safety Abroad";
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Get in touch with Dental Safety Abroad. Questions, feedback, or press enquiries — we'd love to hear from you."
      );
    }
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute("content", originalDesc);
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("phone_alt");
    if (honeypot && String(honeypot).trim()) {
      setStatus("success");
      form.reset();
      return;
    }

    // Remove honeypot from payload
    formData.delete("phone_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d976lgttfb6ioa7s1vlg", {
        method: "POST",
        body: formData,
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";

      if (
        response.ok &&
        parsed?.code === "OK" &&
        !serverMsg.toLowerCase().includes("spam") &&
        !serverMsg.toLowerCase().includes("form data is spam")
      ) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setFormError("Network error. Please check your connection and try again.");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative bg-[#182940] pt-24 md:pt-28">
        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 lg:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <nav
              className="flex items-center justify-center gap-2 text-xs text-white/50 mb-6"
              aria-label="Breadcrumb"
            >
              <Link to="/" className="hover:text-white/80 transition-colors">
                Home
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-white/70" aria-current="page">
                Contact
              </span>
            </nav>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
              Get in Touch
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-2xl mx-auto">
              Questions, feedback, corrections, or press enquiries — we read
              everything and aim to respond within 2–3 working days.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Layout */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
            {/* Left: Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
                  Contact Information
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 leading-tight mb-3">
                  How to Reach Us
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                  We're a small, independent team. We don't offer personal dental
                  advice or clinic recommendations — but we're happy to answer
                  questions about our guides, flag an error in our content, or
                  discuss partnership enquiries.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line text-sm"></i>
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">Email</div>
                    <a
                      href="mailto:amber@dentalsafetyabroad.com"
                      className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      amber@dentalsafetyabroad.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-global-line text-sm"></i>
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">Website</div>
                    <a
                      href="https://dentalsafetyabroad.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      dentalsafetyabroad.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-time-line text-sm"></i>
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">Response time</div>
                    <div className="text-sm text-foreground-600">
                      Usually within 2–3 working days
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-amber-50/70 border border-amber-200/60 rounded-lg p-4 md:p-5">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-amber-100 text-amber-600 mb-2">
                  <i className="ri-alert-line text-sm"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">
                  Important: We Cannot Provide Dental Advice
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  We are not dentists and cannot assess your individual case,
                  recommend specific clinics, or advise on treatment decisions.
                  Please consult a qualified dental professional for personal
                  advice.
                </p>
              </div>

              {/* What to Include */}
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5">
                <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-2">
                  <i className="ri-clipboard-line text-sm"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">
                  What to Include in Your Message
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  Tell us: the page or resource you're referring to, your question
                  or feedback, and the best way to reach you. The more detail you
                  include, the better we can help.
                </p>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-7">
                <h3 className="text-lg font-semibold text-foreground-900 mb-5">
                  Send Us a Message
                </h3>

                {status === "success" ? (
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-check-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-950 mb-1">
                      Message Sent
                    </h3>
                    <p className="text-sm text-foreground-600">
                      Thank you for reaching out. We'll get back to you as soon as
                      possible.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    {/* Honeypot */}
                    <div className="hp-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        Your name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        autoComplete="name"
                        placeholder="Jane Smith"
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        Email address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        autoComplete="email"
                        placeholder="jane@example.com"
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                      >
                        <option value="">— Please select —</option>
                        <option value="general">General enquiry</option>
                        <option value="content">Content question or correction</option>
                        <option value="product">Question about a product or guide</option>
                        <option value="press">Press or media enquiry</option>
                        <option value="partnership">Partnership enquiry</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-foreground-700 mb-1"
                      >
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        required
                        placeholder="Tell us how we can help…"
                        className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-y"
                      ></textarea>
                    </div>

                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="consent"
                        name="consent"
                        required
                        className="w-4 h-4 mt-0.5 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                      />
                      <label htmlFor="consent" className="text-xs text-foreground-600 leading-relaxed">
                        I consent to Dental Safety Abroad storing my details to
                        respond to this enquiry, in line with the{" "}
                        <Link
                          to="/legal"
                          className="text-primary-600 hover:text-primary-700 transition-colors"
                        >
                          privacy policy
                        </Link>
                        .{" "}
                        <span className="text-red-500">*</span>
                      </label>
                    </div>

                    {formError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-700">{formError}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {status === "submitting" ? "Sending…" : "Send Message →"}
                    </button>

                    <p className="text-xs text-foreground-500">
                      Fields marked <span className="text-red-500">*</span> are
                      required. We will never share your details with third parties
                      or send unsolicited marketing emails.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}